import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import type { ConnectionStatus } from '@/features/realtime/types/realtime.types';
import { useAppSelector } from '@/hooks/useAppSelector';

export interface RealtimeWsBadgeLabels {
  dot: string;
  label: string;
  subtle: string;
}

/**
 * Nhãn + dòng “cập nhật telemetry” cho chỉ báo luồng IoT (map header, quản lý trạm, v.v.).
 */
export function useRealtimeWsBadge(): {
  wsBadge: RealtimeWsBadgeLabels;
  telemetryLine: string;
  wsStatus: ConnectionStatus;
} {
  const { t } = useTranslation();
  const wsStatus = useAppSelector((state) => state.realtime.status);
  const lastUpdate = useAppSelector((state) => state.realtime.lastUpdate);

  const wsBadge = useMemo((): RealtimeWsBadgeLabels => {
    if (wsStatus === 'connected') {
      return {
        dot: '#22c55e',
        label: t('realtime.iotNetwork.wsBadgeConnected'),
        subtle: t('realtime.iotNetwork.wsConnected'),
      };
    }
    if (wsStatus === 'connecting') {
      return {
        dot: '#eab308',
        label: t('realtime.iotNetwork.wsBadgeConnecting'),
        subtle: t('realtime.iotNetwork.wsConnecting'),
      };
    }
    return {
      dot: '#ef4444',
      label: t('realtime.iotNetwork.wsBadgeDisconnected'),
      subtle: t('realtime.iotNetwork.wsDisconnected'),
    };
  }, [wsStatus, t]);

  const telemetryLine = useMemo(
    () =>
      lastUpdate != null
        ? t('realtime.iotNetwork.lastTelemetry', {
            time: new Date(lastUpdate).toLocaleString(),
          })
        : t('realtime.iotNetwork.neverUpdated'),
    [lastUpdate, t],
  );

  return { wsBadge, telemetryLine, wsStatus };
}
